import { createTheme } from "@material-ui/core";

interface IPalette {
	main: string;
	light?: string;
}

declare module "@material-ui/core/styles/createPalette" {
	export interface Palette {
		mainText: PaletteOptions["mainText"];
		widgetHeaderText: PaletteOptions["widgetHeaderText"];
		tableText: PaletteOptions["tableText"];
		enabledHeader: PaletteOptions["enabledHeader"];
		iconColor: PaletteOptions["iconColor"];
		tableDivider: PaletteOptions["tableDivider"];
		toolTipBg: PaletteOptions["toolTipBg"];
		grandTotalDivider: PaletteOptions["grandTotalDivider"];
		inputTableDivider: PaletteOptions["inputTableDivider"];
		defaultShadow: PaletteOptions["defaultShadow"];
		activeShadow: PaletteOptions["activeShadow"];
		purple: PaletteOptions["purple"];
		blue: PaletteOptions["blue"];
		link: PaletteOptions["link"];
		charts: Array<string>;
	}
	export interface PaletteOptions {
		mainText: IPalette;
		auth: IPalette;
		widgetHeaderText: IPalette;
		tableText: IPalette;
		enabledHeader: IPalette;
		iconColor: IPalette;
		tableDivider: IPalette;
		toolTipBg: IPalette;
		grandTotalDivider: IPalette;
		inputTableDivider: IPalette;
		defaultShadow: IPalette;
		activeShadow: IPalette;
		purple: IPalette;
		blue: IPalette;
		link: IPalette;
		charts: Array<string>;
	}
}

export const lyfeAuthTheme = createTheme({
	palette: {
		primary: {
			main: "#003538",
			dark: "#367bf5",
			contrastText: "#fff",
		},
		secondary: {
			main: "#ffffff",
			contrastText: "#003538",
		},
		success: {
			main: "#038331",
			light: "#dfeae3",
		},
		warning: {
			main: "#c88824",
			light: "#ffeba5",
		},
		error: {
			main: "#ed544d",
			light: "#fad6d4",
		},
		mainText: {
			main: "#003538",
		},
		widgetHeaderText: {
			main: "#0b5a65",
		},
		tableText: {
			main: "#969392",
		},
		enabledHeader: {
			main: "#5e6e7d",
		},
		iconColor: {
			main: "#7ea6b4",
		},
		tableDivider: {
			main: "#cddcdc",
		},
		toolTipBg: {
			main: "rgba(28, 49, 69, 0.9)",
		},
		grandTotalDivider: {
			main: "#f2fef6",
		},
		inputTableDivider: {
			main: "#f5f8f9",
		},
		defaultShadow: {
			main: "rgba(0, 0, 0, 0.08)",
		},
		activeShadow: {
			main: "rgba(54, 123, 245, 0.6)",
		},
		purple: {
			main: "#614faa",
			light: "#daddfe",
		},
		blue: {
			main: "#7ea6b4",
			light: "#cddcdc",
		},
		text: {
			primary: "#212121",
			secondary: "#5e6e7d",
		},
		link: {
			main: "#18816F",
		},
		charts: [
			"#bf3e93",
			"#614faa",
			"#7c4595",
			"#4c7057",
			"#a87511",
			"#007178",
			"#d386b4",
			"#7e72c6",
			"#a178b4",
			"#82a469",
			"#e3b340",
			"#30b9b1",
			"#e8cedd",
			"#a8afff",
			"#d2bedb",
			"#c6dbb7",
			"#ffde9b",
			"#86edda",
			"#daddfe",
			"#ede4f1",
			"#e8f3d9",
			"#fff4dc",
			"#ccf6f2",
		],
		action: {
			hover: "#f5f8f9",
		},
	} as any,
	overrides: {
		MuiCssBaseline: {
			"@global": {
				body: {
					background: "linear-gradient(180deg, rgba(251,248,245,1) 0%, rgba(255,255,255,1) 100%)",
					backgroundRepeat: "no-repeat",
					backgroundAttachment: "fixed",
				},
			},
		},
		MuiButton: {
			root: {
				borderRadius: "8px",
				"&:hover": {
					boxShadow: "0 0.063rem 0.25rem 0 rgba(54, 123, 245, 0.6)",
				},
				boxShadow: "none",
				fontWeight: "bold",
				padding: "0.313rem 1.5rem",
				height: 36,
				lineHeight: "36px",
				textAlign: "center",
			},
			contained: {
				fontWeight: "bold",
				boxShadow: "none",
				"&:hover": {
					boxShadow: "0 0.063rem 0.25rem 0 rgba(54, 123, 245, 0.6)",
				},
				"&:disabled": {
					backgroundColor: "rgba(54, 123, 245, 0.4)",
					color: "white",
				},
				padding: "0.313rem 1.5rem",
			},
			outlined: {
				fontWeight: "bold",
				"&:hover": {
					boxShadow: "0 0.063rem 0.25rem 0 rgba(54, 123, 245, 0.6)",
					backgroundColor: "white",
				},
				"&:disabled": {
					border: "solid 0.063rem rgba(54, 123, 245, 0.4)",
					color: "rgba(54, 123, 245, 0.4)",
				},
				padding: "0.313rem 1.5rem",
				backgroundColor: "white",
			},
			outlinedPrimary: {
				border: "solid 0.063rem rgba(54, 123, 245, 1)",
				backgroundColor: "white",
				"&:hover": {
					boxShadow: "0 0.063rem 0.25rem 0 rgba(54, 123, 245, 0.6)",
					backgroundColor: "white",
				},
			},
			text: {
				padding: "0.313rem 1.5rem",
				"&:disabled": {
					color: "rgba(54, 123, 245, 0.4)",
				},
			},
		},
		MuiList: {
			root: {
				paddingTop: "0",
				paddingBottom: "0",
				boxShadow: "0 0 1.413rem 0 rgba(0, 0, 0, 0.08)",
			},
			padding: {
				paddingTop: "0",
				paddingBottom: "0",
			},
		},
		MuiFormLabel: {
			asterisk: {
				color: "#367bf5",
			},
			root: {
				color: "#212121",
				alignItems: "center",
			},
		},
		MuiCard: {
			root: {
				borderRadius: "4px",
			},
		},
		MuiFormHelperText: {
			root: {
				color: "#ed544d",
				borderColor: "#ed544d",
				"&$error": {
					color: "#ed544d",
				},
			},
		},
		MuiOutlinedInput: {
			input: {
				padding: "16px 12px",
				fontWeight: 300,
			},
			root: {
				"&:hover .MuiOutlinedInput-notchedOutline": {
					borderColor: "#969392",
					borderWidth: "1px",
				},
				"&.Mui-focused": {
					borderWidth: "1px",
				},
				"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
					borderWidth: "1px",
				},
			},
			multiline: {
				backgroundColor: "white",
			},
		},
		MuiPopover: {
			paper: {
				margin: "6px 0",
				borderTop: 0,
				boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.08)",
				borderRadius: "4px",
			},
		},
		MuiCheckbox: {
			root: {
				"&$checked": {
					"& .MuiIconButton-label": {
						color: "#367bf5",
					},
				},
				color: "#7ea6b4",
			},
			checked: {},
		},
		MuiPaper: {
			elevation1: {
				boxShadow: "0 0 0.5rem 0 rgba(0, 0, 0, 0.08)",
			},
		},
		MuiSwitch: {
			switchBase: {
				// Controls default (unchecked) color for the thumb
				color: "#cddcdc",
				"&.MuiSwitch-colorSecondary.Mui-disabled + .MuiSwitch-track": {
					opacity: 0.4,
					backgroundColor: "#ededed",
				},
				"&.MuiSwitch-colorPrimary.Mui-disabled + .MuiSwitch-track": {
					opacity: 0.1,
				},
			},
			colorPrimary: {
				"&$disabled": {
					color: "#367bf5",
					opacity: 0.4,
				},
			},
			colorSecondary: {
				"&$checked": {
					// Controls checked color for the thumb
					color: "#367bf5",
				},
				"&$disabled": {
					color: "#ededed",
				},
			},
			track: {
				// Controls default (unchecked) color for the track
				backgroundColor: "#cddcdc",
				"$checked$checked + &": {
					// Controls checked color for the track
					opacity: 0.4,
					backgroundColor: "#367bf5",
				},
			},
		},
	},
	typography: {
		fontFamily: "Satoshi, Nunito, sans-serif",
	},
	shadows: [
		"none",
		"0 0 1.413rem 0 rgba(0, 0, 0, 0.08)",
		"0 1.063rem 1.763rem 0 rgba(0, 0, 0, 0.08)",
		"0 0 0.5rem 0 rgba(0, 0, 0, 0.08)",
		"0 0.063rem 0.25rem 0 rgba(54, 123, 245, 0.6)",
		"0 0.063rem 0.25rem 0 rgba(54, 123, 245, 1)",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
		"none",
	],
});
